/**
 * Global Error Service for centralized error management
 * Works with Svelte 5 stores and provides error notification system
 */

import { writable, derived, get } from 'svelte/store';
import type { ErrorState, ErrorSeverity, ErrorContext, ErrorOptions } from '../hooks/useError';

interface GlobalError extends ErrorState {
  id: string;
  createdAt: number;
  dismissedAt?: number;
}

interface ErrorServiceState {
  errors: GlobalError[];
  activeError: GlobalError | null;
  history: GlobalError[];
  maxHistory: number;
}

class ErrorService {
  private state = writable<ErrorServiceState>({
    errors: [],
    activeError: null,
    history: [],
    maxHistory: 50
  });
  
  // Public readable stores
  public readonly errors = derived(this.state, $state => $state.errors);
  public readonly activeError = derived(this.state, $state => $state.activeError);
  public readonly hasErrors = derived(this.state, $state => $state.errors.length > 0);
  public readonly errorCount = derived(this.state, $state => $state.errors.length);
  
  private dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private listeners = new Set<(notification: any) => void>();
  
  /**
   * Add error to the global error queue
   */
  public addError(
    message: string,
    options: ErrorOptions & { id?: string } = {}
  ): string {
    const {
      id = this.generateId(),
      duration = 5000,
      severity = 'error',
      showToUser = true,
      context = {}
    } = options;
    
    const error: GlobalError = {
      id,
      message,
      severity,
      context,
      isVisible: showToUser,
      createdAt: Date.now()
    };
    
    this.state.update(state => {
      // Add to errors queue
      const errors = [...state.errors, error];
      
      // Set as active if no active error
      const activeError = state.activeError || error;
      
      return {
        ...state,
        errors,
        activeError: activeError.id === error.id ? error : activeError
      };
    });
    
    // Notify all listeners
    if (showToUser) {
      const notification = {
        id,
        message,
        severity: options.severity || 'error',
        timestamp: new Date()
      };
      this.listeners.forEach(listener => listener(notification));
    }
    
    // Auto-dismiss if duration is set
    if (duration > 0 && showToUser) {
      this.scheduleDismiss(id, duration);
    }
    
    return id;
  }
  
  /**
   * Dismiss specific error
   */
  public dismissError(id: string) {
    // Clear dismiss timer if exists
    const timer = this.dismissTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.dismissTimers.delete(id);
    }
    
    this.state.update(state => {
      const errors = state.errors.filter(e => e.id !== id);
      const dismissedError = state.errors.find(e => e.id === id);
      
      // Add to history if found
      const history = dismissedError 
        ? [...state.history, { ...dismissedError, dismissedAt: Date.now() }].slice(-state.maxHistory)
        : state.history;
      
      // Update active error if dismissed
      const activeError = state.activeError?.id === id
        ? errors[0] || null
        : state.activeError;
      
      return {
        ...state,
        errors,
        activeError,
        history
      };
    });
  }
  
  /**
   * Clear all errors
   */
  public clearAllErrors() {
    // Clear all timers
    this.dismissTimers.forEach(timer => clearTimeout(timer));
    this.dismissTimers.clear();
    
    this.state.update(state => ({
      ...state,
      errors: [],
      activeError: null
    }));
  }
  
  /**
   * Get error history
   */
  public getHistory(): GlobalError[] {
    return get(this.state).history;
  }
  
  /**
   * Handle API errors with specific patterns
   */
  public handleApiError(error: unknown, context?: ErrorContext): string {
    let message = '알 수 없는 오류가 발생했습니다';
    let severity: ErrorSeverity = 'error';
    
    if (error instanceof Error) {
      message = error.message;
      
      // Check for specific error patterns
      if (message.includes('Network') || message.includes('fetch')) {
        message = '네트워크 연결을 확인해주세요';
        severity = 'warning';
      } else if (message.includes('401') || message.includes('Unauthorized')) {
        message = '인증이 필요합니다';
        severity = 'error';
      } else if (message.includes('403') || message.includes('Forbidden')) {
        message = '접근 권한이 없습니다';
        severity = 'error';
      } else if (message.includes('404')) {
        message = '요청한 리소스를 찾을 수 없습니다';
        severity = 'warning';
      } else if (message.includes('429') || message.includes('Rate limit')) {
        message = '요청 한도를 초과했습니다. 잠시 후 다시 시도해주세요';
        severity = 'warning';
      } else if (message.includes('500') || message.includes('Internal')) {
        message = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요';
        severity = 'error';
      }
    }
    
    return this.addError(message, {
      severity,
      context: {
        ...context,
        metadata: {
          ...context?.metadata,
          type: 'api',
          originalError: error
        }
      }
    });
  }
  
  /**
   * Handle validation errors
   */
  public handleValidationError(
    field: string,
    message: string,
    context?: ErrorContext
  ): string {
    return this.addError(message, {
      severity: 'warning',
      duration: 3000,
      context: {
        ...context,
        metadata: {
          ...context?.metadata,
          type: 'validation',
          field
        }
      }
    });
  }
  
  /**
   * Show success message
   */
  public showSuccess(message: string, duration = 3000): string {
    return this.addError(message, {
      severity: 'success' as any,  // Success severity for notifications
      duration,
      context: {
        metadata: {
          type: 'success'
        }
      }
    });
  }
  
  /**
   * Show info message
   */
  public showInfo(message: string, duration = 3000): string {
    return this.addError(message, {
      severity: 'info',
      duration
    });
  }
  
  /**
   * Show warning message
   */
  public showWarning(message: string, duration = 4000): string {
    return this.addError(message, {
      severity: 'warning',
      duration
    });
  }
  
  /**
   * Subscribe to notifications
   */
  public subscribe(callback: (notification: any) => void): () => void {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }
  
  /**
   * Create error handler for async operations
   */
  public createAsyncHandler<T>(
    operation: string,
    context?: ErrorContext
  ) {
    return async (fn: () => Promise<T>): Promise<[T | null, string | null]> => {
      try {
        const result = await fn();
        return [result, null];
      } catch (error) {
        const errorId = this.handleApiError(error, {
          ...context,
          action: operation
        });
        return [null, errorId];
      }
    };
  }
  
  private scheduleDismiss(id: string, duration: number) {
    const timer = setTimeout(() => {
      this.dismissError(id);
    }, duration);
    
    this.dismissTimers.set(id, timer);
  }
  
  private generateId(): string {
    return `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const errorService = new ErrorService();

// Export convenience functions
export const { 
  addError, 
  dismissError, 
  clearAllErrors,
  handleApiError,
  handleValidationError,
  showSuccess,
  showWarning,
  createAsyncHandler
} = errorService;